/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { FEE_TYPES } from '../src/shared/constants/orderDetails';
import {
    AttributeCategory,
    AvailabilityType,
    CampsiteReserveType,
    FacilityType,
    OrderStatus,
    ReservationFlag,
    ReservationStatus,
    SalesChannel,
    ScheduleType,
    TypeOfUse,
} from './enums';
import type { QueueLottery } from './lottery';

export type Activity = {
    activity_description: string;
    activity_fee_description: string;
    activity_id: number;
    activity_name: string;
    created_date: string;
    is_deactivated: boolean;
    updated_date: string;
};

export type AccessInfo = {
    access_info_id: string;
    created_by: string;
    created_date: string;
    end_date: string;
    instructions: string;
    resource_id: string;
    resource_type: string;
    security_code: string;
    start_date: string;
    updated_by: string;
    updated_date: string;
};

export type Amenity = {
    attribute_code: string;
    attribute_id: number;
    attribute_name: string;
    attribute_value: string;
    created_date: string;
    updated_date: string;
};

export type EquipmentDetail = {
    attribute_code: string;
    attribute_id: number;
    attribute_name: string;
    attribute_value: string;
    created_date: string;
    updated_date: string;
};

export type EquipmentDetailMap = Record<string, EquipmentDetail>;

export type Address = {
    address1: string;
    address2: string;
    address3: string;
    address_type: string;
    asset_address_id: string;
    city: string;
    country_code: string;
    created_date: string;
    is_deactivated: boolean;
    postal_code: string;
    state_code: string;
    updated_date: string;
};

export type LookUp = {
    campsite_details_labels: {
        isValid: boolean;
        results: {
            bed_type: string;
            campfire_allowed: string;
            campsite_reserve_type: string;
            campsite_type: string;
            capacity_rating: string;
            checkin_time: string;
            checkout_time: string;
            condition_rating: string;
            driveway_entry: string;
            driveway_grade: string;
            driveway_length: string;
            driveway_surface: string;
            electric_hookup: string;
            hike_in_distance: string;
            is_accessible: string;
            is_double_driveway: string;
            is_equip_mandatory: string;
            location_rating: string;
            max_num_horses: string;
            max_num_people: string;
            max_num_vehicles: string;
            max_vehicle_length: string;
            min_num_people: string;
            min_num_vehicles: string;
            num_bedrooms: string;
            num_beds: string;
            num_rooms: string;
            pets_allowed: string;
            proximity_water: string;
            shade: string;
            shower_bath_type: string;
            site_access: string;
            site_height: string;
            site_length: string;
            site_rating: string;
            site_width: string;
            tent_pad_length: string;
            tent_pad_width: string;
            type_of_use: string;
            water_hookup: string;
        };
    };
    holidays: {
        isValid: boolean;
        results: Record<string, string[][]>;
    };
};

export type Campground = {
    activated_date: string;
    activities: Activity[];
    addresses: Address[];
    alternate_names: string;
    amenities: Record<string, string>;
    attributes: any;
    cancellation_description: string;
    certification_log: any;
    city: string;
    created_by: string;
    created_date: string;
    facility_adaaccess: string;
    facility_description_map: any;
    facility_directions: string;
    facility_email: string;
    facility_email_display: boolean;
    facility_id: string;
    facility_latitude: number;
    facility_longitude: number;
    facility_lookups: any;
    facility_map_url: string;
    facility_name: string;
    facility_phone: string;
    facility_reservation_url: string;
    facility_rules: any;
    facility_time_zone: string;
    facility_type: FacilityType;
    facility_use_fee_description: string;
    fee_model: number;
    inventory_type_id: string;
    is_commercially_managed: boolean;
    is_deactivated: boolean;
    legacy_facility_id: string;
    links: any;
    notices: any;
    order_components: any;
    org_code: string;
    org_id: string;
    parent_asset_id: string;
    queue_lotteries: Record<string, QueueLottery>;
    receipt_autoprint: boolean;
    receipt_custom_text: string;
    state: string;
    stay_limit: string;
    updated_by: string;
    updated_date: string;
};

export type CampsiteAttribute = {
    attribute_id: number;
    attribute_name: string;
    attribute_value: string;
    attribute_category: AttributeCategory;
};

export type CampsiteEquipment = {
    campsite_equipment_type_id?: number;
    equipment_name: string;
    max_length?: number;
    created_date?: string;
    is_deactivated?: boolean;
    updated_date?: string;
};

export type Template = {
    facility_id: string;
    template_id: string;
    season_type: string;
};

export type Selection = {
    startDate: string; // UTC date string
    endDate: string; // UTC date string
    actualEndDate: string;
    name?: string;
    loop?: string;
    campsiteId?: string;
    dateRangeMap?: Record<string, string>;
    numOccupants?: number;
};

export type Selections = Record<string, Selection>; // maps a campsite id to a selection

export type Rate = {
    deposit: number;
    effective_date: string;
    future_fees: any | null;
    group_fees: any | null;
    holiday: number;
    increment_fees: any | null;
    partial_payment: number;
    per_night: number;
    per_person: number;
    site_type: string;
    template_id: string;
    template_name: string;
    weekend: number;
};

export type Season = {
    facility_id: string;
    season_description: string;
    season_effective_date: string;
    season_end: string;
    season_start: string;
    season_type: string;
    name_map: Record<string, string>;
    price_map: Record<string, number>;
    rate_map: Record<string, Rate>;
    site_type_map: Record<string, string>;
};

export type Schedule = {
    start_date: string;
    end_date: string;
    schedule_id: string;
    status: string;
    campsiteId: string;
};

export type ScheduleLog = {
    Incomplete: boolean;
    actions: {
        send_email: boolean;
        cancel_reservations: boolean;
        full_refund: boolean;
    };
    campsite_ids: string[];
    comment: string;
    created_by: string;
    created_date: string;
    email_text: string;
    end_date: string;
    facility_id: string;
    is_bulk: false;
    origin_location: string;
    purpose: string;
    reason: string;
    refund_amount: number;
    schedule_id: string;
    schedule_type: ScheduleType;
    start_date: string;
    status: AvailabilityType;
    updated_by: string;
    updated_date: string;
};

export type TaxSchedule = {
    amount: string;
    created_by: string;
    created_date: string;
    description: string;
    end_date: string;
    facility_id: string;
    secondary_type: string;
    secondary_value: string;
    site_type: string;
    start_date: string;
    tax_schedule_id: string;
    updated_by: string;
    updated_date: string;
};

export type BusinessRule = {
    description: string;
    end_date: string;
    secondary_value: string;
    start_date: string;
    units: string;
    value: number;
};

export type CampsiteRules = Record<string, BusinessRule>;
export type FacilityRules = Record<string, BusinessRule>;

export type FeeTemplates = {
    'Off Peak': string;
    Peak: string;
    Transition: string;
    'Walk In': string;
};

export type CampsiteNotice = {
    notice_text: string;
    notice_type: 'info' | 'warning' | 'error';
};

export type Campsite = {
    amenities: Amenity[];
    attributes: CampsiteAttribute[];
    campsite_id: string;
    campsite_latitude: number;
    campsite_longitude: number;
    campsite_name: string;
    campsite_name_change: any;
    campsite_reserve_type: string;
    campsite_status: string;
    campsite_type: string;
    campsite_rules: CampsiteRules;
    created_by: string;
    created_date: string;
    equipment_details_map: EquipmentDetailMap;
    facility_id: string;
    fee_templates: FeeTemplates;
    field_latitude: number;
    field_longitude: number;
    accessible: string;
    is_deactivated: boolean;
    legacy_id: string;
    loop: string;
    notices: CampsiteNotice[];
    permitted_equipment: CampsiteEquipment[];
    position_date: string;
    recurring_closures: any[];
    site_details_map: EquipmentDetailMap;
    site_type_config: any;
    supplemental_camping: any;
    type_of_use: TypeOfUse;
    updated_by: string;
    updated_date: string;
};

type Equipment = {
    equipment_name: string;
    max_length: number;
};

export type SeasonFeeTotal = {
    template_id: string;
    maxFee: number;
    minFee: number;
    perPersonFee: number;
    season_type: string;
    site_type: string;
};

export type SearchCampsiteRatePrice = {
    amount: number;
    attribute: string;
};

export type SearchCampsiteRateFee = {
    deposit: number;
    holiday: number;
    per_night: number;
    per_person: number;
    weekend: number;
};

export type SearchCampsiteRateMap = Record<
    string,
    {
        group_fees: SearchCampsiteRateFee | null;
        single_amount_fees: SearchCampsiteRateFee | null;
    }
>;

export type SearchCampsiteRate = {
    end_date: string;
    prices: SearchCampsiteRatePrice[];
    rate_map: SearchCampsiteRateMap;
    season_type: string;
    start_date: string;
    season_description: string;
};

export type AvailableDateRange = {
    end_date: string;
    start_date: string;
};

export type NextAvailableResult = {
    asset_id: string;
    next_available_dates: AvailableDateRange[];
};

export type SearchCampsite = {
    availability: string;
    available_dates?: AvailableDateRange[];
    accessible: string;
    asset_id: string;
    asset_name: string;
    asset_type: string;
    attributes: CampsiteAttribute[];
    campsite_id: string;
    campsite_reserve_type: 'Site-Specific' | 'Non Site-Specific' | 'Overflow';
    campsite_status: string;
    city: string;
    country_code: string;
    fee_templates: FeeTemplates;
    latitude: string;
    longitude: string;
    loop: string;
    name: string;
    org_id: string;
    org_name: string;
    parent_asset_id: string;
    parent_asset_name: string;
    parent_asset_type: string;
    preview_image_url?: string;
    permitted_equipment: Equipment[];
    reservable: boolean;
    price_range?: {
        amount_max: number;
        amount_min: number;
        per_unit: string;
    };
    rates: SearchCampsiteRate[];
    state_code: string;
    type: string;
    type_of_use: TypeOfUse;
};

export enum CampsiteAvailableTypes {
    CAMPSITE_AVAILABLE = 'Available',
    CAMPSITE_NOT_AVAILABLE = 'Not Available',
    CAMPSITE_NOT_RESERVABLE = 'Not Reservable',
    CAMPSITE_NOT_RESERVABLE_MNG = 'Not Reservable Management',
    CAMPSITE_RESERVED = 'Reserved',
    CAMPSITE_CURRENT = 'Current',
    CAMPSITE_OPEN = 'Open',
    CAMPSITE_UNAVAILABLE = 'Unavailable',
    CAMPSITE_CLOSED = 'Closed',
    CAMPSITE_LOTTERY = 'Lottery',
    CAMPSITE_LOTTERY_OPEN = 'Lottery - Open',
    CAMPSITE_EARLY_ACCESS = 'Early Access',
    CAMPSITE_NOT_YET_RELEASED = 'NYR',
    CAMPSITE_RESERVABLE_TABLE_VALUE = 'Reservable',
    CAMPSITE_NOT_AVAILABLE_CUTOFF = 'Not Available Cutoff',
    CAMPSITE_NOT_RESERVABLE_CUTOFF = 'Not Reservable Cutoff',
    //local availabilities
    CAMPSITE_EARLY_ACCESS_AVAILABLE = 'Early Access - Available',
    CAMPSITE_RESERVED_AVAILABLE = 'Reserved Available',
    CAMPSITE_CHECKOUT_ONLY = 'Checkout',
    CAMPSITE_WALK_UP = 'Walk-up',
}
export type CampsiteAvailabilities = Record<string, CampsiteAvailableTypes>; // maps a date to an availability status

export type LocalCampsite = SearchCampsite & {
    availabilities: CampsiteAvailabilities;
    availabilityStatus: CampsiteAvailableTypes;
    numConsecutiveAvailable: number;
};

export type Fee = {
    amount: number;
    attribute: string;
    description: string;
    fee_type: keyof typeof FEE_TYPES;
    sku: string;
};

export type RefundValue = {
    cost: string;
    end_date: string;
    is_late: boolean;
    no_refund: boolean;
    refund: string;
    start_date: string;
};

export type AccountInfo = {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    state: string;
    zip: string;
};

export type LineItem = {
    fee: Fee;
    quantity: number;
};

export type ReservationFeeData = {
    deposit?: {
        fee: Fee;
        quantity: number;
    }[];
    has_partial_payment: boolean;
    has_per_person_fees: boolean;
    minimum_payment: {
        fee: Fee;
        quantity: number;
    };
    no_show_value: RefundValue;
    refund_values: RefundValue[];
    standard_fees: LineItem[];
    taxes: LineItem[];
    transaction_fees: LineItem[];
};

export type ReservationNight = {
    campsite_id: string;
    campsite_loop: string;
    campsite_name: string;
    campsite_reserve_type: CampsiteReserveType;
    campsite_type: string;
    num_people: number;
    refund_amount: number;
};

export type NightMap = Record<string, ReservationNight>;

export type PaymentPlan = {
    final_payment: boolean;
    next_payment_amount: string;
    next_payment_due: string;
    next_payment_total: string;
    next_payment_until: string;
    overdue_reminder_sent: string;
    paid_until: string;
    payment_plan_type: 'partialPayment' | 'recurringMonthlyPayment';
    recurring_payment_due_days: number;
    total_amount_due: string;
};

export type Pass = {
    pass_type: string;
    pass_number: string;
};

export type SiteOccupant = {
    custom_order_details: Record<string, object>;
    num_occupants: number;
    num_vechicles: number;
    pass: Pass;
    primary_occupant: AccountInfo;
};

export type Reservation = {
    access_info: any;
    account_id: string;
    account_info: any;
    account_info_map: AccountInfo;
    campsite_id: string;
    campsite_loop: string;
    campsite_name: string;
    charged_modification_amount: number;
    created_by: string;
    created_date: string;
    end_date: string;
    extra_info: Record<string, string>;
    facility_id: string;
    facility_type: FacilityType;
    fee_data: ReservationFeeData;
    flags: Record<ReservationFlag, boolean>;
    issuance_data: {
        copy_number: number;
    };
    is_past?: boolean;
    line_items: LineItem[];
    night_map: NightMap;
    nights: string[];
    order_id: string;
    order_status: OrderStatus;
    payment_plan: PaymentPlan | null;
    reservation_id: string;
    reservation_number: string;
    reservation_options: any;
    reservation_status: ReservationStatus;
    resource_id?: string;
    quantity?: number;
    sales_channel: SalesChannel;
    site_occupant: SiteOccupant;
    start_date: string;
    status_history: Record<string, ReservationStatus>;
    status_log: Record<
        string,
        {
            status: ReservationStatus;
            user: string;
        }
    >;
    subtotal: string;
    supplemental_selection: SupplementalSelectionMap;
    total_cost: string;
    total_discount: string;
    total_paid: string;
    total_tax: string;
    updated_by: string;
    updated_date: string;
    violations: string[];
};

export type SupplementalCamping = {
    deactivated_date: string;
    description: string;
    is_deactivated: boolean;
    item_id: string;
    name: string;
    price: string;
    quantity: number;
    type: string;
};

/** Selected supplemental item window (CampingService SupplementalSelection). */
export type SupplementalSelection = {
    item_id: string;
    start_date: string;
    end_date: string;
    quantity: number;
    name?: string;
    description?: string;
};

/** Map of supplemental item id -> selections for that item. */
export type SupplementalSelectionMap = Record<string, SupplementalSelection[]>;

export type AvailabilityCampsite = {
    availabilities: CampsiteAvailabilities;
    campsite_id: string;
    campsite_reserve_type: string;
    campsite_rules: CampsiteRules;
    campsite_type: string;
    capacity_rating: string;
    hide_external: boolean;
    loop: string;
    max_num_people: number;
    min_num_people: number;
    quantities: Record<string, number>;
    site: string;
    supplemental_camping: Record<string, SupplementalCamping>;
    type_of_use: TypeOfUse;
};
export type PriceDetailsByReservation = Record<string, PriceDetails>;

export type PriceDetails = {
    line_items: LineItem[];
    subtotal: number;
    total_cost: number;
    pending_balance: number;
    total_discount: number;
    total_tax: number;
    reservation_id?: string;
    refund_values?: RefundValue[];
    payment_plan?: PaymentPlan;
};

export type Release = {
    end: string;
    group_end: string;
    group_release_time: string;
    group_sliding_end: string;
    release_time: string;
    sliding_end: string;
};

export type CampsiteReleaseSchedule = {
    current_release: Release;
    next_release: Release;
    sites: ReservationNight[];
};

export type CampgroundReleases = {
    current_release: Release;
    next_release: Release;
    late_releases: Record<string, CampsiteReleaseSchedule>;
    other_releases?: Record<string, CampsiteReleaseSchedule>;
};
